import React, { useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { RoomMockup } from '../fabric/RoomMockup';
import { ROOM_SCENES, sceneAmbiences, resolveScene } from '../fabric/roomScenes';
import { classifyFabric } from '../fabric/fabricLight';
import { resolveImageUrl } from '../../lib/imageCache';
import { usePreviewPrefs } from './usePreviewPrefs';
import { sceneThumb } from '../../lib/sceneImages';
import { ROMAN_FOLDS, FOLD_INFO, linedLight, useRomanLining } from '../../lib/romanFold';

const abs = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

/* AI scene for a fold: flat → main room scenes (default room first); hobbled/relaxed → their own scenes */
const foldMap = (color, fold) => (fold === 'flat' ? color?.room_visualizations : color?.roman_fold_visualizations?.[fold]) || {};
/* Prefer one room every fold has, so the comparison is like-for-like */
const sharedRoom = (color) => {
  const maps = ROMAN_FOLDS.map(([f]) => foldMap(color, f)).filter(m => Object.values(m).some(Boolean));
  const common = maps.length ? Object.keys(maps[0]).filter(r => maps.every(m => m[r])) : [];
  return common.includes(color?.default_room) ? color.default_room : common[0];
};
const aiSceneFor = (color, fold, room) => {
  const map = foldMap(color, fold);
  return sceneThumb(abs(map[room] || map[color?.default_room] || Object.values(map).find(Boolean)), 512);
};

/* Side-by-side Flat / Hobbled / Relaxed for the chosen fabric — AI scene where available, otherwise the Simulator */
export const CompareFoldsDialog = ({ open, onOpenChange, selectedColor, selectedCollection, selectedMaterial, config, currentFold, onChoose }) => {
  const [view, setView] = useState('simulator');
  const [{ roomId, ambience }] = usePreviewPrefs();
  const [lining] = useRomanLining();
  const room = ROOM_SCENES.find(s => s.id === roomId) || ROOM_SCENES[0];
  const amb = sceneAmbiences(room).includes(ambience) ? ambience : sceneAmbiences(room)[0];
  const scene = useMemo(() => resolveScene(room, amb), [room, amb]);
  const base = useMemo(() => classifyFabric(selectedColor, selectedMaterial,
    { collection_name: selectedCollection?.name, fabric_type: selectedCollection?.fabric_type || config?.fabric_material, openness: selectedCollection?.openness }),
  [selectedColor, selectedMaterial, selectedCollection, config?.fabric_material]);
  const shown = linedLight(base, base.opacity || 0.85, lining);
  const texture = resolveImageUrl(selectedColor?.image_url, 512);
  const room0 = useMemo(() => sharedRoom(selectedColor), [selectedColor, open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[92vh] overflow-y-auto" data-testid="compare-folds-dialog">
        <DialogHeader><DialogTitle>Compare Roman folds{selectedColor?.name ? ` · ${selectedColor.name}` : ''}</DialogTitle></DialogHeader>
        <div className="flex rounded-lg bg-neutral-100 p-0.5 w-fit" data-testid="compare-folds-view">
          {[['simulator', 'Simulator'], ['ai', 'Scenes']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setView(k)} data-testid={`compare-view-${k}`}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${view === k ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500'}`}>{label}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {ROMAN_FOLDS.map(([fold, label]) => {
            const ai = view === 'ai' ? aiSceneFor(selectedColor, fold, room0) : null;
            const active = currentFold === fold;
            return (
              <div key={fold} className={`rounded-xl border overflow-hidden transition-colors ${active ? 'border-neutral-900 ring-2 ring-neutral-900' : 'border-neutral-200'}`} data-testid={`compare-fold-${fold}`}>
                <div className="relative bg-neutral-100" style={{ aspectRatio: '3/4' }}>
                  {ai ? <img src={ai} alt={`${label} roman fold`} className="absolute inset-0 w-full h-full object-cover" data-testid={`compare-fold-${fold}-ai`} />
                    : <RoomMockup scene={scene} ambience={amb} textureUrl={texture} hexColor={selectedColor?.hex_color} light={shown.light} filterOpacity={shown.filterOpacity} drop={0.6} animate={false} containerAspect={4 / 3} system="roman" fold={fold} />}
                  {view === 'ai' && !ai && <span className="absolute top-2 left-2 z-30 bg-white/90 text-[10px] font-semibold px-2 py-0.5 rounded-full" data-testid={`compare-fold-${fold}-sim-note`}>No Scene yet · Simulator</span>}
                </div>
                <div className="p-2 flex flex-col gap-1.5">
                  <div><p className="font-medium text-sm text-neutral-900">{label}</p><p className="text-[10px] text-neutral-500 leading-snug" data-testid={`compare-fold-${fold}-info`}>{FOLD_INFO[fold]}</p></div>
                  <Button size="sm" variant={active ? 'default' : 'outline'} className="h-8 text-xs gap-1" onClick={() => { onChoose(fold); onOpenChange(false); }} data-testid={`compare-choose-${fold}`}>
                    {active ? <><Check className="w-3.5 h-3.5" /> Selected</> : 'Choose this fold'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
